import { Injectable } from '@angular/core';
import { DateService } from '../../../../shared/services/date.service';
import moment from 'moment';
import { FormConfig, FormNavigationConfig, DefaultFormConfig } from '@ptl/forms';
import { NgRedux } from '@ptl/angular-redux-store';
import { PlansStateStructure } from '../../../plan-selection/configs/interfaces/plansStateStructure.interface';
import { ReducerUtilityService } from '../../../../shared/utility-services/reducer-utility.service';
import { StateStructure } from '@ptl/state';
import { savePartAEffectiveDate } from '../../../../shared/reducers/enrollment.reducer';
import { MedigapFormSubmit } from '../../../plan-selection/configs/interfaces/medigap-form-submitted.interface';
import { saveMedigapValuesPostFormSubmit, plansNeedRefreshedFromBackend } from '../../../plan-selection/configs/reducers/plans.reducer';
import { ApplicationQualificationStateStructure } from '../../../shared/interfaces/applicationQualificationStateStructure.interface';
import { HttpHeaders } from '@angular/common/http';
import { RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES, HTTPWrapperContext } from '@ptl/xhr';
import { environment } from '../../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PagePreauthorizationService {

    ageEvent = {
        formControlName: 'ageCheck',
        type: 'valueChange',
        value: 'valid'
    };

    constructor(
        private httpClient: HTTPWrapperContext,
        private dateUtility: DateService,
        private reducerUtilityService: ReducerUtilityService,
        protected ngRedux: NgRedux<StateStructure>,
    ) { }

    isUserEligibleForMacra(formConfig: DefaultFormConfig, dob: string, partA: string): boolean {
        const userDobDate = new Date(this.dateUtility.getDateAsFormattedString(dob, 'MM/DD/YYYY'));
        const userPartADate = new Date(this.dateUtility.getDateAsFormattedString(partA, 'MM/DD/YYYY'));
        const partAMaxDate = new Date(this.dateUtility.getDateAsFormattedString(formConfig.meta.macraPartAMaxDate, 'MM/DD/YYYY'));
        const dobMaxDate = new Date(this.dateUtility.getDateAsFormattedString(formConfig.meta.macraBirthdayMaxDate, 'MM/DD/YYYY'));

        // If user was born after the dobMaxDate and the part A effective date entered is not on or before partAMaxDate then we don't show MACRA plans
        if (userDobDate > dobMaxDate && userPartADate > partAMaxDate) {
            return false;
        }

        return true;
    }

    configureAgeCheck(ageCheckConfig, subscriptionGroup, pageFormGroups, formStreamTrigger) {
        // assign values from config or use default ones if none supplied
        let formName = ageCheckConfig.connectPath ? ageCheckConfig.connectPath : 'enrollment';
        let coverageDateFormName = ageCheckConfig.coverageDateName ? ageCheckConfig.coverageDateName : 'coverageDate';
        let dobFormName = ageCheckConfig.dobName ? ageCheckConfig.dobName : 'dob';
        let minYears = ageCheckConfig.minYears ? ageCheckConfig.minYears : 65;
        let minMonths = ageCheckConfig.minMonths ? ageCheckConfig.minMonths : 0;
        let minDays = ageCheckConfig.minDays ? ageCheckConfig.minDays : 0;

        // Listen for form changes and recheck the date values
        subscriptionGroup.push(pageFormGroups[formName].valueChanges.subscribe((val) => {
        let dob = val[dobFormName];
        let thresholdDate = ageCheckConfig.thresholdKey === 'coverageDate' ? val[coverageDateFormName] : moment();

        // Validate we have data before continuing
        if (!val[dobFormName] || !thresholdDate ||
            !pageFormGroups[formName].controls[dobFormName].valid || 
            !pageFormGroups[formName].controls[coverageDateFormName].valid) {
            return;
        }

        // Go back the specified amount of time from our threshold date and then compare against the dob
        let latestDob = moment.parseZone(thresholdDate)
            .subtract(minYears, 'years')
            .subtract(minMonths, 'months')
            .subtract(minDays, 'days');

        if (latestDob.isBefore(dob)) {
            this.ageEvent.value = 'invalid';
        }
        else {
            this.ageEvent.value = 'valid';
        }

        formStreamTrigger.triggerFormStream(this.ageEvent);
        }));
    }

    async saveMedigapDataToState(formConfig: DefaultFormConfig, additionalPayloadOptions?: any) {
      const tobaccoUsage = formConfig.form.customConfigs?.overrideTobaccoValue || this.reducerUtilityService.getPayloadState('enrollment.tobaccoUsage');
      const planState:PlansStateStructure = this.reducerUtilityService.getState('Plans');
      const partAEffectiveDate:string = this.reducerUtilityService.getPayloadState('enrollment.preAuthPartAEffectiveDate')
        || this.reducerUtilityService.getPayloadState('enrollment.partAEffectiveDate');
  
      const preauthorizationPayload: MedigapFormSubmit = {
        gender: this.reducerUtilityService.getPayloadState('personalInfo.gender'),
        macra: this.reducerUtilityService.getPayloadState('enrollment.macra'),
        whb: this.reducerUtilityService.getPayloadState('enrollment.whb'),
        tobaccoUsage,
        dob: this.dateUtility.getDateAsFormattedString(this.reducerUtilityService.getPayloadState('enrollment.dob'), 'YYYY-MM-DD'),
        coverageDate: this.dateUtility.getDateAsFormattedString(this.reducerUtilityService.getPayloadState('enrollment.coverageDate'), 'YYYY-MM-DD'),
        hardStops: planState.medigapValuesPostFormSubmit && planState.medigapValuesPostFormSubmit.hardStops || 'true'
      };

      // Apply any custom configurations that may have been passed in
      Object.assign(preauthorizationPayload, additionalPayloadOptions);
  
      // Extra logic surronding MACRA plans
      if (!this.isUserEligibleForMacra(formConfig, preauthorizationPayload.dob, partAEffectiveDate)) {
        preauthorizationPayload.macra = 'FALSE';
      }
  
      if (partAEffectiveDate) {
        this.ngRedux.dispatch(savePartAEffectiveDate(this.dateUtility.getDateAsFormattedString(partAEffectiveDate, 'MM/DD/YYYY')));
      }
  
      this.ngRedux.dispatch(saveMedigapValuesPostFormSubmit(preauthorizationPayload));
      this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
      //this.ngRedux.dispatch(shouldHideMedigapEligibilityForm(true));
    }

    getApplicationQualification(): Promise<ApplicationQualificationStateStructure> {
      const payloadInPayload = {
        initialEntryDate: this.dateUtility.getDateAsFormattedString(new Date().toString(), 'MM/DD/YYYY') || "",

        applicantBirthDate: this.dateUtility.getDateAsFormattedString(this.reducerUtilityService.getPayloadState('enrollment.dob'), 'MM/DD/YYYY') || "",
        applicationEffectiveDate: this.dateUtility.getDateAsFormattedString(this.reducerUtilityService.getPayloadState('enrollment.coverageDate'), 'MM/DD/YYYY') || "",
        partAEffectiveDate: this.dateUtility.getDateAsFormattedString(this.reducerUtilityService.getPayloadState('enrollment.partAEffectiveDate'), 'MM/DD/YYYY') || "",
        partBEffectiveDate: this.dateUtility.getDateAsFormattedString(this.reducerUtilityService.getPayloadState('enrollment.partBEffectiveDate'), 'MM/DD/YYYY') || "",

        gauranteedGI: this.reducerUtilityService.getPayloadState('medigapEligibilityCheck.guaranteedIssue') || "",
        bcbsSubscriber: this.reducerUtilityService.getPayloadState('enrollment.bcbsSubscriber') || null,
        guaranteedEligible: this.reducerUtilityService.getPayloadState('medigapEligibilityCheck.guaranteedAcceptance') || "",
        upgradeCoverage: this.reducerUtilityService.getPayloadState('medigapEligibilityCheck.upgradeCurrentCoverage') || "",
        nonMedigapBlue: this.reducerUtilityService.getPayloadState('medigapEligibilityCheck.enrolledInNonMedigapBlue'),
        within6MonthsOfAge65: this.reducerUtilityService.getPayloadState('medigapEligibilityCheck.within6MonthsOfAge65') || "",
        partEnrolled: this.reducerUtilityService.getPayloadState('medigapEligibilityCheck.within6MonthsOfPartBEnrollment') || "",
        esrdQuestion: this.reducerUtilityService.getPayloadState('medigapEligibilityCheck.medicareForRenalDisease') || null,
        region: this.reducerUtilityService.getComponentStates().ZipCounty.county.state || "",
        currentMedicarePolicyAndBirthday: this.reducerUtilityService.getPayloadState('medigapEligibilityCheck.currentMedicarePolicyAndBirthday') || null
      };
  
      const payload = {
        baseMessage: {
          headerVersion: '1.0'
        },
        payload: payloadInPayload
      }
  
      const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.POST,
        url: '/cmcsnr/applicationQualification',
        headers: new HttpHeaders({
          'x-hmhs-test-backend': environment.external.engen.eBill.overrideEnv
        }),
        POST_OR_PUT_PAYLOAD: payload
      });
  
      this.httpClient.setStrategy(XHR_STRATEGIES.WAS_INTER);
      return this.httpClient.makeRequest(requestConfig);
    }
}
